import Divider from "@amzn/meridian/divider";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Alert from "@amzn/meridian/alert";
import { formatISO } from "date-fns";
import { useAppSelector } from "src/store/store";
import { useState, useEffect } from "react";
import Button from "@amzn/meridian/button";
import BotMessage from "src/components/shared/botMessage/botMessage";
import UserMessage from "src/components/shared/userMessage/userMessage";
import FavoriteToggleButton from "src/components/pages/home/components/chat/favoriteToggleButton";
import SourcesDropdown from "src/components/shared/sourcesDropDown/sourcesDropdown";
import CitationText from "src/components/shared/citationText/citationText";
import MarkdownRenderer from "src/components/shared/markdownRenderer/markdownRenderer";
import CsvTable from "src/components/shared/csvTable/csvTable";
import ChartViewer from "src/components/shared/chartViewer/chartViewer";
import ToolCallSection from "./toolCallSection";
import Vote from "../vote/vote";
import { processCitations } from "src/utils/citationUtils";
import { downloadCSV, getCsvColumnCount } from "src/utils/csvUtils";
import { CSV_PREVIEW_MESSAGE } from "src/constants/constants";
import "./streamingMessage.scss";

const StreamingMessage = () => {
  const {
    streaming,
    streamingHistory,
    recentInput,
    isInputDisabled,
    showResult,
  } = useAppSelector((state) => state.chatSlice);
  const { alias } = useAppSelector((state) => state.user);
  const [dots, setDots] = useState("");
  const [showChart, setShowChart] = useState<{ [key: string]: boolean }>({});

  const shouldShowChartButton = (csvPreview: string) => {
    return getCsvColumnCount(csvPreview) <= 4;
  };

  useEffect(() => {
    if (isInputDisabled && showResult) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isInputDisabled, showResult]);

  useEffect(() => {
    if (streaming.isStreaming) {
      setTimeout(() => {
        const inputElement = document.getElementById("chat-input-text-area");
        if (inputElement) {
          inputElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [streaming.messageSegments.length]);

  useEffect(() => {
    if (streamingHistory.length > 0) {
      setTimeout(() => {
        const inputElement = document.getElementById("chat-input-text-area");
        if (inputElement && !isInputDisabled) {
          inputElement.focus();
          inputElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [streamingHistory.length, isInputDisabled]);

  return (
    <>
      {/* Show completed streaming responses */}
      {streamingHistory.map((historyItem) => (
        <div
          key={historyItem.messageId}
          id={historyItem.messageId}
          className="message-container streaming-message"
        >
          <div className="user-message-row">
            <UserMessage alias={alias} datetime={historyItem.datetime}>
              {historyItem.userText}
            </UserMessage>
            <FavoriteToggleButton messageId={historyItem.messageId} />
          </div>

          <BotMessage
            datetime={historyItem.datetime}
            className="streaming-bot-message"
          >
            {/* Status indicator */}
            <div className="streaming-status completed">
              <Text type="h100" color="success">
                Completed
              </Text>
            </div>

            {/* Show tool calls that were executed */}
            {historyItem.toolCalls.map((toolCall) => {
              return (
                <div
                  key={toolCall.tool_use_id}
                  className="streaming-tool-calls"
                >
                  <ToolCallSection toolCall={toolCall} />
                </div>
              );
            })}

            {/* Show final result */}
            {historyItem.showCsv && historyItem.csvPreview ? (
              <div>
                {showChart[historyItem.messageId] ? (
                  <div>
                    <ChartViewer csvPreview={historyItem.csvPreview} />
                    <Row spacingInset={"300 none none none"}>
                      <Button
                        onClick={() =>
                          setShowChart((prev) => ({
                            ...prev,
                            [historyItem.messageId]: false,
                          }))
                        }
                      >
                        Show CSV Preview
                      </Button>
                    </Row>
                  </div>
                ) : (
                  <div>
                    <div style={{ margin: "16px 0" }}>
                      <Alert type="theme" size="large">
                        {CSV_PREVIEW_MESSAGE}
                      </Alert>
                    </div>
                    <CsvTable
                      csvPreview={historyItem.csvPreview}
                      csvData={historyItem.csvData}
                      hideDownloadButton={true}
                    />
                    <Row spacingInset={"300 none none none"}>
                      <Button
                        onClick={() =>
                          historyItem.csvData &&
                          downloadCSV(historyItem.csvData)
                        }
                      >
                        Download CSV
                      </Button>
                      {shouldShowChartButton(historyItem.csvPreview) && (
                        <Button
                          onClick={() =>
                            setShowChart((prev) => ({
                              ...prev,
                              [historyItem.messageId]: true,
                            }))
                          }
                        >
                          Generate Chart
                        </Button>
                      )}
                    </Row>
                  </div>
                )}
              </div>
            ) : historyItem.sourceAttributions &&
              historyItem.sourceAttributions.length > 0 ? (
              <div className="streaming-content">
                <CitationText
                  text={processCitations(
                    historyItem.currentMessage,
                    historyItem.sourceAttributions,
                  )}
                  citations={historyItem.sourceAttributions}
                />
              </div>
            ) : (
              <div className="streaming-content">
                <MarkdownRenderer content={historyItem.currentMessage} />
              </div>
            )}

            {/* Add SAIGE related documents if available */}
            {historyItem.relatedDocuments &&
              historyItem.relatedDocuments.length > 0 && (
                <>
                  <Divider spacingBefore="400" spacingAfter="400" />
                  <SourcesDropdown
                    relatedDocuments={historyItem.relatedDocuments}
                    sourceAttributions={historyItem.sourceAttributions}
                  />
                </>
              )}

            {/* Adding Vote component to get feedback from users */}
            <Vote
              messageId={historyItem.messageId}
              llmResponse={{ tool_name: "mcp_tools" }}
              voteResponse={historyItem.voteResponse || ""}
              thumbsUpClicked={historyItem.thumbsUpClicked || false}
              thumbsDownClicked={historyItem.thumbsDownClicked || false}
            />
          </BotMessage>
        </div>
      ))}

      {/* Show current streaming message when request is in progress */}
      {isInputDisabled && showResult && (
        <div className="message-container streaming-message">
          <div className="user-message-row">
            <UserMessage alias={alias} datetime={formatISO(new Date())}>
              {recentInput}
            </UserMessage>
          </div>

          <BotMessage
            datetime={formatISO(new Date())}
            className="streaming-bot-message"
          >
            {/* Status indicator */}
            <div
              className={`streaming-status ${isInputDisabled ? "active" : "completed"}`}
            >
              <Text type="h100" color={isInputDisabled ? "primary" : "success"}>
                {isInputDisabled ? (
                  <span className="thinking-text">
                    Thinking<span className="thinking-dots">{dots}</span>
                  </span>
                ) : (
                  "Completed"
                )}
              </Text>
            </div>

            {/* Display streaming message segments in order */}
            {streaming.messageSegments.map((segment) => (
              <div key={segment.id}>
                {segment.type === "text" && segment.content ? (
                  <div className="streaming-content">
                    <MarkdownRenderer content={segment.content} />
                  </div>
                ) : segment.type === "tool_call" && segment.toolCall ? (
                  <div className="streaming-tool-calls">
                    <ToolCallSection
                      key={segment.toolCall.tool_use_id}
                      toolCall={segment.toolCall}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </BotMessage>
        </div>
      )}
    </>
  );
};

export default StreamingMessage;
